import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FreeMode, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import ElapsedTime from '../../../common/components/elapsed-time/ElapsedTime';
import { appSubscriptionService } from '../../../common/subscriptions/appSubscriptionService';
import { DispenseLog, DispenseState } from '../../api/dispenseLogs/DispenseLog';
import {
  useAcknowledgeDispenseLogVoidMutation,
  useGetActiveDispenseLogsQuery,
  useReprintDispenseLogMutation,
  useUpdateDispenseLogStateMutation,
} from '../../api/dispenseLogs/dispenseLogsApiSlice';
import '../style.sass';
import './failed-prints.sass';

import 'swiper/css/bundle';

interface StatusMessage {
  id: number;
  type: 'success' | 'error';
  message: string;
}

const operatorsForLog = (items: { operatorName?: string }[]): string => {
  const names = Array.from(new Set(items.map((i) => i.operatorName).filter((n): n is string => !!n)));
  return names.length > 0 ? names.join(', ') : '-';
};

function FailedPrints() {
  const navigate = useNavigate();
  const { data: dispenseLogs, isLoading, refetch } = useGetActiveDispenseLogsQuery();
  const [reprintDispenseLog, { isLoading: isReprinting }] = useReprintDispenseLogMutation();
  const [updateDispenseLogState, { isLoading: isUpdating }] = useUpdateDispenseLogStateMutation();
  const [acknowledgeDispenseLogVoid, { isLoading: isAcknowledging }] = useAcknowledgeDispenseLogVoidMutation();
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [pendingStateChange, setPendingStateChange] = useState<{
    log: DispenseLog;
    state: DispenseState;
    label: string;
  } | null>(null);

  // Filter dispense logs that are not served
  const pendingLogs =
    dispenseLogs?.filter((log) => {
      return log.state !== DispenseState.Served;
    }) || [];

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      if ('data' in error) return String(error.data);
      if ('error' in error) return String(error.error);
    }
    return String(error);
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);

      return newSet;
    });
  };

  const handleAcknowledgeVoid = async (log: DispenseLog) => {
    try {
      await acknowledgeDispenseLogVoid(log.id).unwrap();
      refetch();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'error', message: errorMessage }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 5000);
    }
  };

  const handlePrint = async (log: DispenseLog) => {
    try {
      await reprintDispenseLog(log.id).unwrap();
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'success', message: 'Print sent successfully' }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 3000);
      refetch();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'error', message: errorMessage }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 5000);
    }
  };

  const handleStateChange = async (log: DispenseLog, newState: DispenseState, label: string) => {
    try {
      await updateDispenseLogState({
        id: log.id,
        request: { state: newState },
      }).unwrap();
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'success', message: `Marked as ${label}` }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 200);
      refetch();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'error', message: errorMessage }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 5000);
    }
  };

  const getStateLabel = (state: DispenseState): string => {
    switch (state) {
      case DispenseState.Pending:
        return 'Pending';
      case DispenseState.Preparing:
        return 'Preparing';
      case DispenseState.ReadyToServe:
        return 'Ready to Serve';
      case DispenseState.Served:
        return 'Served';
    }
  };

  const getStateModifier = (state: DispenseState): string => {
    switch (state) {
      case DispenseState.Pending:
        return 'pending';
      case DispenseState.Preparing:
        return 'preparing';
      case DispenseState.ReadyToServe:
        return 'ready';
      case DispenseState.Served:
        return 'served';
    }
  };

  // Subscribe to SignalR events for dispense logs changes
  useEffect(() => {
    const sub1 = appSubscriptionService.failedDispensePrintsChangedObservable().subscribe(() => {
      refetch();
    });
    const sub2 = appSubscriptionService.dispenseLogStateChangedObservable().subscribe(() => {
      refetch();
    });
    const sub3 = appSubscriptionService.dispenseLogVoidedObservable().subscribe(() => {
      refetch();
    });
    const sub4 = appSubscriptionService.dispenseLogVoidAcknowledgedObservable().subscribe(() => {
      refetch();
    });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
      sub3.unsubscribe();
      sub4.unsubscribe();
    };
  }, [refetch]);

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') navigate(-1);
        }}
      >
        <div className="pos-modal pos-modal_role_failed-prints">
          <div className="pos-modal__header">
            <h2 className="pos-modal__title pos-modal__title_role_failed-prints">Active Dispense Orders</h2>
          </div>

          {pendingStateChange && (
            <div className="pos-modal__popup">
              <div className="pos-popup pos-popup_role_confirmation">
                <div className="pos-popup__content">
                  <div className="pos-popup__info">
                    <h3 className="pos-popup__title">Confirm</h3>
                    <p className="pos-popup__label">Mark as {pendingStateChange.label}</p>
                    <p className="pos-popup__text">
                      #{pendingStateChange.log.transactionNumber} · Table {pendingStateChange.log.tableNumber || '-'}
                    </p>
                    <p className="pos-popup__text">
                      {operatorsForLog(pendingStateChange.log.items)} · {pendingStateChange.log.items.length} item
                      {pendingStateChange.log.items.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="pos-popup__actions">
                    <button
                      className="pos-popup__action pos-popup__action_role_no"
                      type="button"
                      onClick={() => setPendingStateChange(null)}
                    >
                      No
                    </button>
                    <button
                      className="pos-popup__action pos-popup__action_role_yes"
                      type="button"
                      disabled={isUpdating}
                      onClick={() => {
                        const pending = pendingStateChange;
                        setPendingStateChange(null);
                        handleStateChange(pending.log, pending.state, pending.label);
                      }}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="pos-modal__content">
            {isLoading && <div className="pos-modal__loading">Loading...</div>}

            {!isLoading && pendingLogs.length === 0 && <div className="pos-modal__empty">No active dispense orders found</div>}

            {!isLoading && pendingLogs.length > 0 && (
              <div className="pos-failed-prints">
                <div className="pos-failed-prints__scroll">
                  <Swiper
                    freeMode={true}
                    height={500}
                    slidesPerView="auto"
                    spaceBetween={0}
                    mousewheel={true}
                    scrollbar={true}
                    direction="vertical"
                    pagination={{ clickable: true }}
                    modules={[FreeMode, Pagination]}
                  >
                    <SwiperSlide>
                      <div className="pos-failed-prints__content">
                        <table className="pos-failed-prints__table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Transaction #</th>
                              <th>Table #</th>
                              <th>Device</th>
                              <th>Operator</th>
                              <th>Time</th>
                              <th>Status</th>
                              <th>Items</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingLogs.map((log) => {
                              const statusMsg = statusMessages.find((msg) => msg.id === log.id);
                              const isExpanded = expandedRows.has(log.id);
                              return (
                                <>
                                  <tr key={log.id} className={`pos-failed-prints__row${log.isVoid ? ' pos-failed-prints__row_void' : ''}`}>
                                    <td>
                                      <button
                                        className="pos-failed-prints__expand-btn"
                                        onClick={() => toggleRow(log.id)}
                                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                      >
                                        {isExpanded ? '▼' : '▶'}
                                      </button>
                                    </td>
                                    <td>{log.transactionNumber}</td>
                                    <td>{log.tableNumber || '-'}</td>
                                    <td>{log.orderDeviceName}</td>
                                    <td>{operatorsForLog(log.items)}</td>
                                    <td>
                                      <ElapsedTime since={log.dateCreated} />
                                    </td>
                                    <td>
                                      {log.isVoid ? (
                                        <span className="pos-failed-prints__void-badge">VOID</span>
                                      ) : (
                                        <span
                                          className={`pos-failed-prints__state-badge pos-failed-prints__state-badge_${getStateModifier(
                                            log.state,
                                          )}`}
                                        >
                                          {getStateLabel(log.state)}
                                        </span>
                                      )}
                                    </td>
                                    <td>{log.items.length}</td>
                                    <td>
                                      {log.isVoid ? (
                                        <div className="pos-failed-prints__void-actions">
                                          <span className="pos-failed-prints__void-badge">VOID</span>
                                          <button
                                            className="pos-failed-prints__action pos-failed-prints__action_role_acknowledge"
                                            onClick={() => handleAcknowledgeVoid(log)}
                                            disabled={isAcknowledging}
                                          >
                                            Acknowledge
                                          </button>
                                        </div>
                                      ) : statusMsg ? (
                                        <div className={`pos-failed-prints__status pos-failed-prints__status_${statusMsg.type}`}>
                                          {statusMsg.message}
                                        </div>
                                      ) : (
                                        <>
                                          <button
                                            className="pos-failed-prints__action"
                                            onClick={() => handlePrint(log)}
                                            disabled={isReprinting || isUpdating}
                                          >
                                            Print
                                          </button>
                                          {log.state === DispenseState.Pending && (
                                            <button
                                              className="pos-failed-prints__action pos-failed-prints__action_role_preparing"
                                              onClick={() =>
                                                setPendingStateChange({ log, state: DispenseState.Preparing, label: 'Preparing' })
                                              }
                                              disabled={isReprinting || isUpdating}
                                            >
                                              Preparing
                                            </button>
                                          )}
                                          {log.state === DispenseState.Preparing && (
                                            <button
                                              className="pos-failed-prints__action pos-failed-prints__action_role_ready"
                                              onClick={() =>
                                                setPendingStateChange({ log, state: DispenseState.ReadyToServe, label: 'Ready to Serve' })
                                              }
                                              disabled={isReprinting || isUpdating}
                                            >
                                              Ready to Serve
                                            </button>
                                          )}
                                          {log.state === DispenseState.ReadyToServe && (
                                            <button
                                              className="pos-failed-prints__action pos-failed-prints__action_role_resolve"
                                              onClick={() => setPendingStateChange({ log, state: DispenseState.Served, label: 'Served' })}
                                              disabled={isReprinting || isUpdating}
                                            >
                                              Served
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr key={`${log.id}-items`} className="pos-failed-prints__items-row">
                                      <td colSpan={9}>
                                        <div className="pos-failed-prints__items">
                                          <h4>Items to make:</h4>
                                          <ul className="pos-failed-prints__items-list">
                                            {log.items.map((item) => (
                                              <li
                                                key={item.id}
                                                className={`pos-failed-prints__item${item.isVoid ? ' pos-failed-prints__item_void' : ''}`}
                                              >
                                                <div className="pos-failed-prints__item-main">
                                                  <span className="pos-failed-prints__item-qty">{item.quantity}x</span>
                                                  <span className="pos-failed-prints__item-desc">{item.description}</span>
                                                  {item.isVoid && <span className="pos-failed-prints__item-void-label">VOID</span>}
                                                </div>
                                                {item.childItems && item.childItems.length > 0 && (
                                                  <ul className="pos-failed-prints__child-items">
                                                    {item.childItems.map((childItem, index) => (
                                                      <li key={`${item.id}-child-${index}`} className="pos-failed-prints__child-item">
                                                        <span className="pos-failed-prints__child-desc">{childItem.description}</span>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FailedPrints;
